import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, Loader2, MoreHorizontal, Church, Phone, Globe, MapPin } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { ChurchProfileForm } from "@/components/ChurchProfileForm";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import api from "@/lib/api";
import { toast } from "sonner";
import { RevenueDashboard } from "@/components/RevenueDashboard";

const STATE_STYLE = {
  active: ["Active", "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"],
  warning: ["Grace period", "bg-amber-500/15 text-amber-300 border-amber-500/30"],
  final_warning: ["Final warning", "bg-orange-500/15 text-orange-300 border-orange-500/30"],
  locked: ["Locked", "bg-red-500/15 text-red-400 border-red-500/30"],
};
const REASON = {
  subscribed: "Stripe subscription", legacy: "Existing church", trial: "Free trial", trial_ended: "Trial ended",
  cancelled: "Cancelled / expired", payment_failed: "Payment failed", admin_override: "Set by admin", comp: "Comped (free)", no_subscription: "No subscription",
};
const FILTERS = [["all", "All"], ["active", "Active"], ["expiring", "Expiring"], ["locked", "Locked"]];

export default function Subscriptions() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [profileOf, setProfileOf] = useState(null);

  const load = () => api.get("/admin/billing/subscriptions").then((r) => setData(r.data)).catch(() => toast.error("Couldn't load subscriptions"));
  useEffect(() => { load(); }, []);

  const override = async (row, value) => {
    try { await api.post(`/admin/users/${row.user_id}/billing-override`, { override: value }); toast.success("Access updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't update"); }
  };

  const rows = (data?.rows || []).filter((r) => filter === "all" || (filter === "expiring" ? ["warning", "final_warning"].includes(r.state) : r.state === filter));
  const c = data?.counts || {};

  return (
    <div className="p-6 lg:p-8 max-w-[1300px] mx-auto">
      <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-3"><CreditCard className="h-7 w-7 text-amber-400" /> Subscriptions</h1>
      <p className="text-gray-400 mt-1.5 text-sm">Every church's billing status. Locked churches can only reach Billing and Support; their streams and players are paused.</p>

      <RevenueDashboard />

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[["active", "Active", c.active], ["warning", "Grace period", c.warning], ["final_warning", "Final warning", c.final_warning], ["locked", "Locked", c.locked]].map(([k, l, n]) => (
          <div key={k} data-testid={`subs-count-${k}`} className="bg-card border border-gray-800 rounded-xl p-4">
            <p className="text-xs text-gray-500">{l}</p>
            <p className="text-2xl font-display font-bold mt-1">{n ?? "—"}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 flex gap-2">
        {FILTERS.map(([k, l]) => (
          <button key={k} data-testid={`subs-filter-${k}`} onClick={() => setFilter(k)} className={"px-3 py-1.5 rounded-lg text-sm border transition-colors " + (filter === k ? "border-amber-500/50 text-amber-300 bg-amber-500/10" : "border-gray-800 text-gray-400 hover:text-white")}>{l}</button>
        ))}
      </div>

      <div className="mt-4 bg-card border border-gray-800 rounded-2xl overflow-x-auto">
        {!data ? <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-400" /></div> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wide text-gray-500">
              <th className="px-5 py-3 font-medium">Church</th><th className="px-4 py-3 font-medium">Plan</th><th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Why</th><th className="px-4 py-3 font-medium">Days left</th><th className="px-4 py-3 font-medium">Renews / ends</th><th className="px-4 py-3" />
            </tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={7} className="px-5 py-10 text-center text-gray-500" data-testid="subs-empty">No churches in this view.</td></tr> : rows.map((r) => {
                const [label, cls] = STATE_STYLE[r.state] || STATE_STYLE.active;
                return (
                  <tr key={r.user_id} data-testid={`subs-row-${r.user_id}`} className="border-t border-gray-800/70 hover:bg-gray-800/30">
                    <td className="px-5 py-3">
                      <button data-testid={`subs-open-profile-${r.user_id}`} onClick={() => setProfileOf(r)} className="flex items-center gap-3 text-left group">
                        <span className="h-9 w-9 rounded-lg bg-gray-950 border border-gray-800 flex items-center justify-center overflow-hidden shrink-0">
                          {r.logo_url ? <img src={r.logo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${r.logo_url}` : r.logo_url} alt="" className="h-full w-full object-contain" /> : <Church className="h-4 w-4 text-gray-600" />}
                        </span>
                        <span className="min-w-0">
                          <span className="block text-white font-medium group-hover:text-amber-300">{r.organization || r.name || r.email}</span>
                          <span className="block text-xs text-gray-500">{r.name && r.organization ? `${r.name} · ` : ""}{r.email}</span>
                          <span data-testid={`subs-profile-summary-${r.user_id}`} className="flex flex-wrap gap-x-3 text-[11px] text-gray-500 mt-0.5">
                            {r.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{r.phone}</span>}
                            {(r.city || r.addr_state) && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{[r.city, r.addr_state].filter(Boolean).join(", ")}</span>}
                            {r.website && <span className="inline-flex items-center gap-1"><Globe className="h-3 w-3" />{r.website.replace(/^https?:\/\//, "")}</span>}
                          </span>
                        </span>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-gray-300">{r.plan_name || "—"}</td>
                    <td className="px-4 py-3"><span data-testid={`subs-state-${r.user_id}`} className={`text-[11px] font-semibold px-2 py-1 rounded-md border ${cls}`}>{label}</span></td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{REASON[r.reason] || r.reason}{r.cancel_at_period_end ? " · cancels at period end" : ""}</td>
                    <td className="px-4 py-3 text-gray-300 tabular-nums">{r.days_left ?? "—"}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{r.current_period_end ? new Date(r.current_period_end).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger data-testid={`subs-actions-${r.user_id}`} className="h-8 w-8 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 inline-flex items-center justify-center"><MoreHorizontal className="h-4 w-4" /></DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-gray-900 border-gray-800 text-gray-200">
                          <DropdownMenuItem data-testid={`subs-view-profile-${r.user_id}`} onClick={() => setProfileOf(r)} className="cursor-pointer focus:bg-gray-800">View / edit profile</DropdownMenuItem>
                          <DropdownMenuItem data-testid={`subs-client-page-${r.user_id}`} onClick={() => navigate(`/clients/${r.user_id}`)} className="cursor-pointer focus:bg-gray-800">Open client page</DropdownMenuItem>
                          <DropdownMenuItem data-testid={`subs-force-active-${r.user_id}`} onClick={() => override(r, "active")} className="cursor-pointer focus:bg-gray-800">Keep active (comp)</DropdownMenuItem>
                          <DropdownMenuItem data-testid={`subs-force-expired-${r.user_id}`} onClick={() => override(r, "expired")} className="cursor-pointer focus:bg-gray-800 text-red-300">Lock now</DropdownMenuItem>
                          {r.override && <DropdownMenuItem data-testid={`subs-clear-override-${r.user_id}`} onClick={() => override(r, null)} className="cursor-pointer focus:bg-gray-800">Follow Stripe status</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <Sheet open={!!profileOf} onOpenChange={(v) => !v && setProfileOf(null)}>
        <SheetContent data-testid="profile-panel" side="right" className="bg-card border-gray-800 text-white w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-white font-display">{profileOf?.organization || "Church profile"}</SheetTitle>
            <SheetDescription className="text-gray-400">{profileOf?.email} · {profileOf?.plan_name || "No plan"}</SheetDescription>
          </SheetHeader>
          <div className="mt-6">
            {profileOf && <ChurchProfileForm compact admin base={`/admin/churches/${profileOf.user_id}/profile`} logoPath={`/admin/churches/${profileOf.user_id}/logo`} onSaved={load} />}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
